import { Button, Menu, MenuItem, Popover, Intent } from "@snpjs/core";
import { Download, FileCsv, MicrosoftExcelLogo } from "@snpjs/icons";
import * as XLSX from "xlsx";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Routes } from "../../routes/Routes";

import { AdminViewApi } from "../../api/AdminViewApi";
import { ILicenseLogEntry } from "../../shared/model/ILicenseLogEntry";
import { Table2 } from "../../components/table2/Table2";
import { texts } from "../../i18n/texts";
import { useRequest } from "../../hooks/useRequest";
import useTranslation from "../../hooks/useTranslation";
import styles from "./AdminViewSection.module.scss";

const parseJsonObject = (value: unknown): Record<string, any> | null => {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return parsed !== null && typeof parsed === "object" ? parsed : null;
    } catch {
      return null;
    }
  }

  return typeof value === "object" ? (value as Record<string, any>) : null;
};

export const flattenExtendedProjectRows = (
  rows: ILicenseLogEntry[]
): ILicenseLogEntry[] => {
  return rows.flatMap((row) => {
    const productEntries = parseJsonObject(row.TY_PROD_TAB);
    const productItems = Array.isArray(productEntries)
      ? productEntries
      : productEntries
      ? [productEntries]
      : [];

    if (productItems.length === 0) {
      return [
        {
          CUSTOMER_ID: row.CUSTOMER_ID ?? "",
          PROJECT_ID: row.PROJECT_ID ?? "",
          PRODUCT_ID: "",
          CONTR_START_DATE: "",
          CONTR_END_DATE: "",
          MAINT_START_DATE: "",
          MAINT_END_DATE: "",
          LICENSE_OPTION_ID: "",
          IS_DEACTIVATED: row.IS_DEACTIVATED ?? "",
          NO_SYNC: row.NO_SYNC ?? "",
          OBJECT_ID: row.OBJECT_ID ?? "",
          SREQ_ID: row.SREQ_ID ?? "",
        },
      ];
    }

    return productItems.map((product) => {
      const licenseInfo = parseJsonObject(product?.TY_LIC_TAB);
      const licenseOptionId = licenseInfo?.LICENSE_OPTION_ID ?? "";

      return {
        CUSTOMER_ID: row.CUSTOMER_ID ?? "",
        PROJECT_ID: row.PROJECT_ID ?? "",
        PRODUCT_ID: product?.PRODUCT_ID ?? "",
        CONTR_START_DATE: product?.CONTR_START_DATE ?? "",
        CONTR_END_DATE: product?.CONTR_END_DATE ?? "",
        MAINT_START_DATE: product?.MAINT_START_DATE ?? "",
        MAINT_END_DATE: product?.MAINT_END_DATE ?? "",
        LICENSE_OPTION_ID: licenseOptionId,
        IS_DEACTIVATED: row.IS_DEACTIVATED ?? "",
        NO_SYNC: row.NO_SYNC ?? "",
        OBJECT_ID: row.OBJECT_ID ?? "",
        SREQ_ID: row.SREQ_ID ?? "",
      };
    });
  });
};

export const ExtendedProjectTableSection: React.FC = () => {
  const { t } = useTranslation();
  const request = useRequest();
  const navigate = useNavigate();
  const [data, setData] = useState<ILicenseLogEntry[]>([]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [visibleCount, setVisibleCount] = useState<number>(0);

  const fetch = async () => {
    setIsLoading(true);
    await request.send(async () => {
      const rows = await AdminViewApi.getSwitchTable();
      setData(flattenExtendedProjectRows(rows));
      setHasLoaded(true);
    });
    setIsLoading(false);
  };

  const exportData = (format: "csv" | "excel", rows: ILicenseLogEntry[] = data) => {
    if (rows.length === 0) return;

    const columns = Object.keys(rows[0] ?? {});
    const escapeCell = (value: unknown): string => {
      const text = value == null ? "" : String(value).replace(/\r?\n/g, " ");
      return `"${text.replace(/"/g, '""')}"`;
    };

    if (format === "csv") {
      const csv = [
        columns.join(","),
        ...rows.map((row) => columns.map((column) => escapeCell((row as Record<string, unknown>)[column])).join(",")),
      ].join("\n");

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `extended_project_table.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      return;
    }

    const workbook = XLSX.utils.book_new();
    const sheetData = rows.map((row) => {
      const values: Record<string, unknown> = {};
      columns.forEach((column) => {
        values[column] = (row as Record<string, unknown>)[column] ?? "";
      });
      return values;
    });

    const worksheet = XLSX.utils.json_to_sheet(sheetData, { header: columns });
    XLSX.utils.book_append_sheet(workbook, worksheet, "ExtendedProjectTable");

    const workbookBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([workbookBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `extended_project_table.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const exportMenu = (table?: any) => (
    <Menu>
      <MenuItem
        icon={<FileCsv />}
        text="CSV"
        onClick={() => exportData("csv", table ? table.getFilteredRowModel().rows.map((row: any) => row.original as ILicenseLogEntry) : data)}
      />
      <MenuItem
        icon={<MicrosoftExcelLogo />}
        text="Excel"
        onClick={() => exportData("excel", table ? table.getFilteredRowModel().rows.map((row: any) => row.original as ILicenseLogEntry) : data)}
      />
    </Menu>
  );

  useEffect(() => {
    void fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={styles.wrapper}>
      <div className={styles.headerRow}>
        <div className={styles.controls}>
          <div
            className={styles.backLink}
            role="button"
            tabIndex={0}
            onClick={() => navigate(`${Routes.adminView.toPath()}/project-table`)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                navigate(`${Routes.adminView.toPath()}/project-table`);
              }
            }}
          >
            &lt; {t(texts.adminView.projectTable)}
          </div>
        </div>
        {hasLoaded && (
          <div className={styles.countHeader}>{visibleCount} Records Found</div>
        )}
      </div>
      
      {hasLoaded && (
        <div className={styles.tableContainer}>
          <Table2
            data={data}
            isLoading={isLoading}
            noResultsTitle={t(texts.adminView.noProjectTableData)}
            onVisibleCountChange={(count) => setVisibleCount(count)}
            showUtilityToolbar={false}
            enableGlobalFilter={false}
            leftToolbar={(table) => (
              <Popover content={exportMenu(table)} placement="bottom-start" usePortal={false}>
                <Button
                  intent={Intent.PRIMARY}
                  icon={<Download />}
                  rightIcon="caret-down"
                  text="Export"
                  fill
                  alignText="left"
                />
              </Popover>
            )}
          />
        </div>
      )}
    </div>
  );
};
